/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useRef, useState } from 'react';
import { Asset, AssetUtils, BookmarkUtils, ContentProps, Ducks } from '@wit/vtv-x-foundation-lib';
import { useSelector } from 'react-redux';
import { StoreState } from 'store/store';
import ImageLoader from '../ImageLoader';
import ProgressBar from '../ProgressBar';
import Badge from '../Badge';

interface Props {
  asset: Asset;
  onAssetClick: (asset: Asset) => void;
  ctxId: number | string;
}

function Portrait({ onAssetClick, asset, ctxId }: Props) {
  const cardId = `${ctxId}-${asset.id}`;
  const bookmark = useSelector((state: StoreState) => Ducks.BookmarksCache.Selectors.getAssetBookmark(state, asset));

  const [customClassName, setCustomClassName] = useState('');
  const [isMouseOver, setIsMouseOver] = useState(false);

  const isEpg = asset && AssetUtils.isEpg(asset);
  const cardImage = asset?.images.card || (isEpg && asset.logo?.color?.uri) || '';

  const customTimeout = useRef<ReturnType<typeof setTimeout>>();

  const handleMouseEnter = () => {
    setIsMouseOver(true);
    customTimeout.current = setTimeout(() => {
      setCustomClassName('card-full-width');
    }, 1000);
  };

  const handleMouseLeave = () => {
    setIsMouseOver(false);
    if (customTimeout && customTimeout.current) {
      clearTimeout(customTimeout.current);
    }
    setCustomClassName('');
  };

  const getAssetUiWithProgress = useCallback(
    (givenAsset: Asset) => {
      if (bookmark) {
        return BookmarkUtils.getProgressFromBookmark(givenAsset as ContentProps, bookmark);
      }
      return undefined;
    },
    [bookmark]
  );

  return (
    <div
      onClick={() => onAssetClick(asset)}
      className={`dynamic-content-card-container ${customClassName}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      id={`Card___${cardId}`}
    >
      <div className="card-title">
        <div className="text">
          <h3 id={`Card_title___${cardId}`}>{asset.title}</h3>
        </div>
        {asset.badges && (
          <div className="card-badges">
            {asset.badges.map((badge) => {
              return <Badge badge={badge} bemBlock="Card" bemModifierPrefix={cardId} key={badge.icon} />;
            })}
          </div>
        )}
        <div className="card-title-background" />
      </div>
      <ImageLoader src={cardImage} bemModifier={cardId} />
      <ProgressBar percentage={getAssetUiWithProgress(asset) || 0} isActive={isMouseOver} bemModifier={cardId} />
    </div>
  );
}

export default React.memo(Portrait);
